<template>
	<header
		class="nav-header p-3 pr-4 flex items-center"
		:class="[isCollapsed ? 'nav-header--collapsed' : 'nav-header--expanded', { 'nav-header--trial' : showUpgradeButton && trialDaysRemaining }]"
	>
		<CogNavButton
			:is-collapsed="isCollapsed"
			:logo-url="logoUrl"
			@toggle-open="$emit('toggle-open')"
			@show-popper="$emit('show-popper', $event)"
			@hide-popper="$emit('hide-popper')"
			@close-popper="$emit('close-popper')"
		/>
		<h1 class="nav-header__org sz-3 mb-0 nowrap--clip">{{ orgName }}</h1>
		<CSupportAndAvatar
			v-if="isMobile && !isCollapsed"
			show-on-mobile
			:avatar="avatar"
			show-support
			size="m"
			class="ml-auto"
			source-context="nav-header"
			@show-account-menu="$emit('show-account-menu')"
		/>
		<div v-else-if="!isCollapsed" class="ml-auto pl-3 shrink-0">
			<RouterLink
				v-if="showUpgradeButton"
				class="button button--primary no-underline"
				:to="`/${orgCode}/settings/plan/select?source=upgrade-now`"
			>
				{{ trialDaysRemaining || 'Upgrade' }}
			</RouterLink>
			<CBadge v-else-if="trialDaysRemaining" :text="trialDaysRemaining" />
			<CBadge v-else-if="planLevel" :text="planLevel.DisplayName" />
		</div>
	</header>
</template>

<script lang="ts">
	import { defineComponent, inject } from 'vue';
	import type { OrganizationMeta } from '@cognitoforms/types/server-types/core/services/organization-meta';
	import CBadge from 'src/components/info/Badge.vue';
	import CogNavButton from './CogNavButton.vue';
	import type { PropType } from 'vue';
	import CSupportAndAvatar from 'src/components/navigation/SupportAndAvatar.vue';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import { useTrialHero } from 'src/composables/use-trial-hero';

	export default defineComponent({
		name: 'CNavHeader',
		components: {
			CogNavButton,
			CBadge,
			CSupportAndAvatar
		},
		props: {
			currentOrg: {
				type: Object as PropType<OrganizationMeta>,
				required: true
			},
			isCollapsed: {
				type: Boolean,
				required: true
			},
			avatar: {
				type: String,
				default: ''
			},
			logoUrl: {
				type: String,
				default: ''
			}
		},
		emits: ['toggle-open', 'show-popper', 'hide-popper', 'close-popper', 'show-account-menu'],
		setup() {
			const isMobile = inject(isMobileKey);
			const { showTrialHero } = useTrialHero();

			return {
				isMobile,
				showTrialHero
			};
		},
		computed: {
			orgName() {
				return this.currentOrg.Name;
			},
			orgCode() {
				return this.currentOrg.Code;
			},
			isOwner() {
				return this.currentOrg.Role === 'Owner';
			},
			isOwnerOrAdmin() {
				return this.currentOrg.Role === 'Owner' || this.currentOrg.Role === 'Administrator';
			},
			planLevel() {
				if (!this.isOwnerOrAdmin)
					return null;
				return this.currentOrg.Plan;
			},
			trialDaysRemaining() {
				if (!this.isOwnerOrAdmin)
					return null;
				if (this.showTrialHero && this.$route?.name === 'home')
					return null;
				const trialDays = this.currentOrg.TrialDaysRemaining;
				if (trialDays > 0)
					return `Trial: ${trialDays} day${trialDays > 1 ? 's' : ''}`;
				return null;
			},
			showUpgradeButton() {
				if (!this.isOwner)
					return false;
				return this.planLevel?.IsPaidPlan === false || this.trialDaysRemaining;
			}
		}
	});
</script>

<style lang="scss">
.nav-header {

	&--expanded {
		width: 100%;
	}

	&--collapsed {
		max-width: var(--nav-width);
		margin-left: env(safe-area-inset-left, 0px);
	}

	:root &__icon {
		width: $size__icon--large;
		height: $size__icon--large;
	}

	&--collapsed &__icon,
	.admin--nav-popper &__icon {
		transform: rotate(180deg);
	}

	/* stylelint-disable-next-line scss/at-rule-no-unknown */
	@container (max-width: 280px) {

		&--trial {

			.nav-header__org {
				display: none;
			}
		}

		.badge {
			display: none;
		}
	}
}

</style>